import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [organisation, setOrganisation] = useState(null);
  const [branches, setBranches] = useState([]);
  const [activeBranchId, setActiveBranchId] = useState(localStorage.getItem("active_branch") || "");
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      setOrganisation(data.organisation);
      setBranches(data.branches);
      // Phase U: persist role so the api.js interceptor can auto-inject branch_id
      // on shared-resource GETs for super-admins who pick an active branch.
      localStorage.setItem("user_role", data.user.role);
      const stored = localStorage.getItem("active_branch");
      if (data.branches?.length && !stored) {
        const allowed = data.user.role === "super_admin"
          ? data.branches
          : data.branches.filter((b) => data.user.branch_ids?.includes(b.id));
        const first = allowed[0] || data.branches[0];
        if (first) {
          setActiveBranchId(first.id);
          localStorage.setItem("active_branch", first.id);
        }
      }
    } catch {
      setUser(false);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      return { success: true, session: data };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const verifyOtp = async (session_token, code) => {
    try {
      const { data } = await api.post("/auth/verify-otp", { session_token, code });
      if (data.token) localStorage.setItem("access_token", data.token);
      await refresh();
      return { success: true, ...data };
    } catch (e) {
      return { success: false, error: formatApiError(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("access_token");
    localStorage.removeItem("active_branch");
    localStorage.removeItem("user_role");
    setUser(false); setOrganisation(null); setBranches([]); setActiveBranchId("");
  };

  const chooseBranch = (id) => {
    setActiveBranchId(id);
    localStorage.setItem("active_branch", id);
  };

  const hasPerm = (key) => user?.role === "super_admin" || !!user?.permissions?.[key];
  const canSeeBranch = (bid) => user?.role === "super_admin" || (user?.branch_ids || []).includes(bid);

  return (
    <AuthContext.Provider value={{
      user, organisation, branches, activeBranchId, loading,
      login, verifyOtp, logout, chooseBranch, hasPerm, canSeeBranch, refresh, setUser,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
